import type {ApiResponseError} from "@/src/config/api/common";
import {useTokenStore} from "@/src/store/use-token-store";
import {storeToRefs} from "pinia";
import {refreshToken} from "@/src/api/sdk.gen";
import type {AxiosError, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import {reactive} from "vue";
import {useMessage} from "@/src/composable/use-message.ts";
import {client} from "@/src/api/client.gen";

export const AUTH_ERROR_CODE: Record<string, ApiResponseError> = {
  ACCESS_DENIED: {
    status: 401,
    code: "AUTH-401-001",
    message: "로그인 권한이 필요합니다.",
  },
  EXPIRED_TOKEN: {
    status: 401,
    code: "AUTH-401-002",
    message: "토큰이 만료되었습니다.",
  },
};

export const handleRequest = (request: InternalAxiosRequestConfig) => {
  const $tokenStore = reactive(useTokenStore());
  if ($tokenStore.accessToken) {
    request.headers.set("Authorization", `Bearer ${$tokenStore.accessToken}`);
  }
  return request;
};

export const handleResponse = (response: AxiosResponse) => {
  return response;
}

export const handleError = async (error: AxiosError<ApiResponseError>) => {
  switch (error.response?.data?.code) {
    case AUTH_ERROR_CODE.ACCESS_DENIED.code:
      return await handleDenied(error);
    case AUTH_ERROR_CODE.EXPIRED_TOKEN.code:
      return await handleExpiredToken(error);
    default:
      const message = error.response?.data?.message ?? "";
      if (message) {
        useMessage().notify(message, "error");
      }
      return Promise.reject(error);
  }
}

const handleDenied = async (error: AxiosError<ApiResponseError>) => {
  const $tokenStore = storeToRefs(useTokenStore());
  $tokenStore.accessToken.value = "";
  $tokenStore.refreshToken.value = "";
  window.location.href = "/api/v1/auth/cafe24";
  return Promise.reject(error);
};

const handleExpiredToken = async (error: AxiosError<ApiResponseError>) => {
  if (!error.config) {
    return Promise.reject(error);
  }
  const $tokenStore = storeToRefs(useTokenStore());
  const { data } = await refreshToken({
    body: {
      refreshToken: $tokenStore.refreshToken.value,
    },
  });
  if (data?.accessToken) {
    $tokenStore.accessToken.value = data.accessToken;
    error.config.headers.Authorization = `Bearer ${ data.accessToken }`;
    return client.instance(error.config);
  } else {
    return await handleDenied(error);
  }
};
